/* This component will render the React-based RestrictedSpace or RestrictedPage components into the older
server-rendered (SSR) "restricted" landing pages.  This is needed because we want to show the Admin Key
entry point or upsell message to Admin users, and those two components know how and when to display the
entry point/upsell. */
import React, { useContext, useEffect, useState } from 'react';

import { ExternalPageNotFoundV2 } from '@confluence/external-share/entry-points/ExternalPageNotFoundV2';
import { LegacyDOMBridge } from '@confluence/legacy-dom-bridge';
import { EXTERNAL_SHARE } from '@confluence/named-routes';
import { RestrictedPage, RestrictedSpace } from '@confluence/no-permission';
import { NotFound } from '@confluence/not-found';
import { RoutesContext } from '@confluence/route-manager';

export const StandaloneRestrictedPageOrSpace = () => {
	const [portalState, setPortalState] = useState(null);
	const { location, matchRoute } = useContext(RoutesContext);

	const hideSSRContentHeader = () => {
		const headerElement = document.querySelector('#main-header');
		if (headerElement) {
			headerElement.style.display = 'none';
		}
	};

	const setPortalStateIfTargetFound = (targetSelector, renderElement) => {
		const target = document.querySelector(targetSelector);
		if (target) {
			setPortalState({ targetSelector, renderElement });
			hideSSRContentHeader();
			return true;
		}
		return false;
	};

	// The legacy 404 / Page Not Found screen does not have the Admin Key entry
	// point or upsell, but it looks outdated so we want to render the more modern
	// React-based 404 screen.
	const setPortalStateIfPageNotFound = () => {
		if (
			document.body?.classList.contains('error-page') &&
			document.querySelector(
				"meta[name='confluence-request-uri'][content='/wiki/fourohfour.action']",
			)
		) {
			const route = matchRoute(`${location.pathname}${location.search}${location.hash}`);
			const NotFoundComponent =
				route?.name === EXTERNAL_SHARE.name ? ExternalPageNotFoundV2 : NotFound;

			setPortalStateIfTargetFound('main#main', () => <NotFoundComponent />);
		}
	};

	useEffect(() => {
		/* We should never have both an SSR restricted page and SSR restricted space at the same time,
    so if we find one we don't have to look for the other (which saves a little execution time).
    Also, note that the RestrictedSpace and RestrictedPage components are wrapped with `Loadable`
    and thus won't be loaded until the function that renders them is actually called (not just defined) */

		setPortalStateIfTargetFound('#restricted-page-portal-container', () => (
			<RestrictedPage accessType="view" />
		)) ||
			setPortalStateIfTargetFound('#restricted-space-portal-container', () => (
				<RestrictedSpace />
			)) ||
			setPortalStateIfPageNotFound();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const renderToPortalTarget = (
		{ targetSelector, renderElement }, // eslint-disable-line react/prop-types
	) =>
		document.querySelector(targetSelector) ? (
			<LegacyDOMBridge selectors={[targetSelector]} portalNode={renderElement()} />
		) : null;

	return portalState ? renderToPortalTarget(portalState) : null;
};

StandaloneRestrictedPageOrSpace.displayName = 'StandaloneRestrictedPageOrSpace';
